import { jsPDF } from 'jspdf'
import type { Presupuesto, PresupuestoItem, Servicio } from '@/types/database'
import type { ConfiguracionEstudio } from '@/services/configuracionService'
import { formatCuit, esPersonaJuridica } from './presupuesto-utils'
import { formatDate } from '@/lib/utils'

interface GeneratePdfOptions {
  presupuesto: Presupuesto
  items: PresupuestoItem[]
  servicios: Servicio[]
  configEstudio?: ConfiguracionEstudio | null
  organizacion?: {
    nombre?: string | null
    branding?: {
      logo_light_url?: string | null
      logo_dark_url?: string | null
    } | null
  } | null
}

// Helper to load image as base64
async function loadImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { mode: 'cors' })
    const blob = await response.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
  }).format(value)
}

// formatCuit and esPersonaJuridica imported from presupuesto-utils

// Helper to get service details
function getServicioDetails(
  servicioId: string,
  servicios: Servicio[]
): { nombre: string; descripcion: string | null } {
  const servicio = servicios.find((s) => s.id === servicioId)
  return {
    nombre: servicio?.nombre ?? 'Servicio no encontrado',
    descripcion: servicio?.descripcion ?? null,
  }
}

// Check if new page is needed
function checkNewPage(
  doc: jsPDF,
  y: number,
  neededHeight: number,
  pageHeight: number,
  currentPage: number,
  totalPages: number,
  options: GeneratePdfOptions,
  logoBase64: string | null
): number {
  if (y + neededHeight > pageHeight - 40) {
    // 40mm for footer
    doc.addPage()
    currentPage++
    drawHeader(doc, options, logoBase64)
    drawFooter(doc, options, logoBase64, currentPage, totalPages)
    return 45 // Y after header
  }
  return y
}

// Draw header (on every page)
function drawHeader(doc: jsPDF, options: GeneratePdfOptions, logoBase64: string | null) {
  const { presupuesto, configEstudio, organizacion } = options

  // Header background
  doc.setFillColor(15, 23, 42) // #0f172a
  doc.rect(0, 0, 210, 35, 'F')

  // Estudio data
  const estudioNombre =
    configEstudio?.nombre || organizacion?.nombre || 'Mi Estudio Contable'

  // Logo or text
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', 15, 10, 0, 15) // auto width, 15mm height
    } catch {
      // Fallback to text
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text(estudioNombre, 15, 20)
    }
  } else {
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text(estudioNombre, 15, 20)
  }

  // Presupuesto number and dates
  const numeroFormateado = (presupuesto.numero_presupuesto || 0).toString().padStart(4, '0')
  const fechaValidez = new Date(presupuesto.fecha || new Date())
  fechaValidez.setDate(fechaValidez.getDate() + (presupuesto.validez_dias ?? 15))

  doc.setTextColor(255, 255, 255)
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text(`Presupuesto #${numeroFormateado}`, 195, 12, { align: 'right' })

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(`Fecha: ${formatDate(presupuesto.fecha || new Date().toISOString())}`, 195, 18, {
    align: 'right',
  })
  doc.text(`Válido hasta: ${formatDate(fechaValidez.toISOString().split('T')[0])}`, 195, 24, {
    align: 'right',
  })
}

// Draw footer (on every page)
function drawFooter(
  doc: jsPDF,
  options: GeneratePdfOptions,
  logoBase64: string | null,
  currentPage: number,
  totalPages: number
) {
  const { configEstudio, organizacion, presupuesto } = options

  const estudioNombre =
    configEstudio?.nombre || organizacion?.nombre || 'Mi Estudio Contable'
  const estudioCuit = configEstudio?.cuit || ''
  const estudioTelefono = configEstudio?.telefono || ''
  const estudioEmail = configEstudio?.email || ''
  const estudioSitioWeb = configEstudio?.sitio_web || ''
  const estudioResponsable = configEstudio?.responsable_nombre || ''
  const estudioMatricula = configEstudio?.matricula_profesional || ''
  const textoLegal =
    configEstudio?.texto_legal_presupuestos ||
    `Presupuesto válido por ${presupuesto.validez_dias ?? 15} días. Los importes podrán modificarse si cambia la normativa fiscal o el alcance del servicio requerido.`

  const footerY = 260 // Start of footer area

  // Separator line
  doc.setDrawColor(226, 232, 240)
  doc.setLineWidth(0.5)
  doc.line(15, footerY, 195, footerY)

  // Left column: Estudio info
  let y = footerY + 6

  // Logo (footer version - light logo preferably)
  const logoUrlFooter =
    organizacion?.branding?.logo_light_url || organizacion?.branding?.logo_dark_url || null

  if (logoUrlFooter && logoBase64) {
    // If footer has different logo, we'd need to load it separately
    // For simplicity, we'll just use text
    doc.setTextColor(30, 41, 59)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text(estudioNombre, 15, y)
    y += 4
  } else {
    doc.setTextColor(30, 41, 59)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text(estudioNombre, 15, y)
    y += 4
  }

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 116, 139)

  if (estudioEmail) {
    doc.text(estudioEmail, 15, y)
    y += 3.5
  }
  if (estudioTelefono) {
    doc.text(`Tel: ${estudioTelefono}`, 15, y)
    y += 3.5
  }
  if (estudioSitioWeb) {
    doc.text(estudioSitioWeb, 15, y)
  }

  // Right column: Responsable info
  let yRight = footerY + 6

  if (estudioResponsable) {
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(30, 41, 59)
    doc.text('Responsable', 195, yRight, { align: 'right' })
    yRight += 4

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 116, 139)
    doc.text(estudioResponsable, 195, yRight, { align: 'right' })
    yRight += 3.5
  }

  if (estudioMatricula) {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 116, 139)
    doc.text(estudioMatricula, 195, yRight, { align: 'right' })
    yRight += 3.5
  }

  if (estudioCuit) {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 116, 139)
    doc.text(`CUIT: ${estudioCuit}`, 195, yRight, { align: 'right' })
  }

  // Legal text
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(148, 163, 184)
  const legalLines = doc.splitTextToSize(textoLegal, 180)
  doc.text(legalLines, 15, 282)

  // Page number
  doc.setFontSize(8)
  doc.setTextColor(100, 116, 139)
  doc.text(`Página ${currentPage} de ${totalPages}`, 105, 292, { align: 'center' })
}

export async function generatePresupuestoPdf(
  options: GeneratePdfOptions
): Promise<jsPDF> {
  const { presupuesto, items, servicios, organizacion } = options

  // Load logo if available
  const logoUrlHeader =
    organizacion?.branding?.logo_dark_url || organizacion?.branding?.logo_light_url || null
  const logoBase64 = logoUrlHeader ? await loadImageAsBase64(logoUrlHeader) : null

  // Initialize PDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  // Constants
  const pageHeight = 297
  let y = 45 // Starting Y after header
  const currentPage = 1
  const totalPages = 1 // Will update later if needed

  // Draw header and footer on first page
  drawHeader(doc, options, logoBase64)
  drawFooter(doc, options, logoBase64, currentPage, totalPages)

  // Section: Cliente data (only on first page)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(100, 116, 139)
  doc.text('DATOS DEL CLIENTE', 15, y)
  y += 6

  // Cliente boxes
  doc.setDrawColor(226, 232, 240)
  doc.setFillColor(248, 250, 252)
  doc.rect(15, y, 85, 15, 'FD')

  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(148, 163, 184)
  const nombreLabel = esPersonaJuridica(presupuesto.cliente_cuit) ? 'RAZÓN SOCIAL' : 'NOMBRE'
  doc.text(nombreLabel, 17, y + 4)

  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 41, 59)
  doc.text(presupuesto.cliente_nombre || '-', 17, y + 10)

  doc.rect(105, y, 90, 15, 'FD')

  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(148, 163, 184)
  doc.text('CUIT', 107, y + 4)

  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 41, 59)
  doc.text(formatCuit(presupuesto.cliente_cuit), 107, y + 10)

  y += 20

  // Admin/Representante (si tiene datos)
  const adminNombre = presupuesto.admin_nombre
  const adminCuit = presupuesto.admin_cuit
  if (adminNombre || adminCuit) {
    const adminLabel = esPersonaJuridica(presupuesto.cliente_cuit) ? 'REP. LEGAL' : 'ADMINISTRADOR'

    doc.setDrawColor(226, 232, 240)
    doc.setFillColor(248, 250, 252)
    doc.rect(15, y, 85, 13, 'FD')

    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(148, 163, 184)
    doc.text(adminLabel, 17, y + 4)

    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(30, 41, 59)
    doc.text(adminNombre || '-', 17, y + 9)

    doc.rect(105, y, 90, 13, 'FD')

    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(148, 163, 184)
    doc.text(`CUIT ${adminLabel}`, 107, y + 4)

    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(30, 41, 59)
    doc.text(formatCuit(adminCuit), 107, y + 9)

    y += 18
  }

  // Section: Detalle de servicios
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(100, 116, 139)
  doc.text('DETALLE DE SERVICIOS', 15, y)
  y += 6

  // Check if any item has discount
  const hayDescuentoEnItems = items.some((item) => (item.descuento_porcentaje ?? 0) > 0)

  // Table header
  doc.setFillColor(234, 126, 52) // #ea7e34
  doc.rect(15, y, 180, 8, 'F')

  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(255, 255, 255)

  doc.text('CONCEPTO', 17, y + 5.5)
  doc.text('CANT.', 135, y + 5.5, { align: 'right' })

  if (hayDescuentoEnItems) {
    doc.text('DTO.%', 148, y + 5.5, { align: 'right' })
    doc.text('PRECIO', 168, y + 5.5, { align: 'right' })
    doc.text('TOTAL', 190, y + 5.5, { align: 'right' })
  } else {
    doc.text('PRECIO', 158, y + 5.5, { align: 'right' })
    doc.text('TOTAL', 190, y + 5.5, { align: 'right' })
  }

  y += 10

  // Table rows
  if (items.length === 0) {
    doc.setFontSize(10)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(148, 163, 184)
    doc.text('No hay items en este presupuesto', 105, y + 10, { align: 'center' })
    y += 20
  } else {
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const servicioDetails = getServicioDetails(item.servicio_id, servicios)
      const descripcionTexto = item.descripcion ?? servicioDetails.descripcion
      const detalleLines =
        descripcionTexto?.split('\n').filter((line) => line.trim()) || []

      // Calculate needed height for this row
      const baseHeight = 6
      const descriptionHeight = detalleLines.length * 3.5
      const neededHeight = baseHeight + descriptionHeight + 2

      // Check if we need a new page
      y = checkNewPage(doc, y, neededHeight, pageHeight, currentPage, totalPages, options, logoBase64)

      // Row background (alternating)
      if (i % 2 === 1) {
        doc.setFillColor(249, 250, 251)
        doc.rect(15, y - 1, 180, neededHeight, 'F')
      }

      // Service name
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 41, 59)
      doc.text(servicioDetails.nombre, 17, y + 4)

      // Description (bullet list)
      if (detalleLines.length > 0) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(71, 85, 105)

        let descY = y + 8
        for (const line of detalleLines) {
          doc.text('•', 19, descY)
          doc.text(line, 23, descY)
          descY += 3.5
        }
      }

      // Cantidad
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(30, 41, 59)
      doc.text((item.cantidad ?? 0).toString(), 135, y + 4, { align: 'right' })

      // Descuento (if applicable)
      if (hayDescuentoEnItems) {
        if ((item.descuento_porcentaje ?? 0) > 0) {
          doc.text(`${item.descuento_porcentaje}%`, 148, y + 4, { align: 'right' })
        } else {
          doc.text('-', 148, y + 4, { align: 'right' })
        }
        doc.text(formatCurrency(item.precio_unitario ?? 0), 168, y + 4, { align: 'right' })
        doc.setFont('helvetica', 'bold')
        doc.text(formatCurrency(item.subtotal ?? 0), 190, y + 4, { align: 'right' })
      } else {
        doc.text(formatCurrency(item.precio_unitario ?? 0), 158, y + 4, { align: 'right' })
        doc.setFont('helvetica', 'bold')
        doc.text(formatCurrency(item.subtotal ?? 0), 190, y + 4, { align: 'right' })
      }

      y += neededHeight + 2
    }
  }

  // Notes section (if exists)
  if (presupuesto.notas) {
    y += 4
    y = checkNewPage(doc, y, 15, pageHeight, currentPage, totalPages, options, logoBase64)

    doc.setDrawColor(226, 232, 240)
    doc.setFillColor(255, 237, 213)
    doc.rect(15, y, 180, 15, 'FD')

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(71, 85, 105)
    const notesLines = doc.splitTextToSize(presupuesto.notas, 170)
    doc.text(notesLines, 17, y + 5)

    y += 18
  }

  // Totals section
  y += 6
  y = checkNewPage(doc, y, 30, pageHeight, currentPage, totalPages, options, logoBase64)

  const subtotalItems = items.reduce((acc, item) => acc + (item?.subtotal ?? 0), 0)
  const descuentoPorcentaje = presupuesto.descuento_porcentaje ?? 0
  const ivaPorcentaje = presupuesto.iva_porcentaje ?? 0
  const descuento = subtotalItems * (descuentoPorcentaje / 100)
  const baseImponible = subtotalItems - descuento
  const iva = baseImponible * (ivaPorcentaje / 100)
  const totalCalculado = baseImponible + iva
  const totalFinal = (presupuesto.total || 0) > 0 ? presupuesto.total : totalCalculado

  doc.setFillColor(15, 23, 42) // #0f172a
  doc.rect(15, y, 180, 30, 'F')

  doc.setTextColor(255, 255, 255)

  // Subtotal
  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.text('Subtotal', 20, y + 6)
  doc.text(formatCurrency(subtotalItems), 190, y + 6, { align: 'right' })

  let yTotal = y + 11

  // Descuento (if any)
  if (descuentoPorcentaje > 0) {
    doc.text(`Descuento (${descuentoPorcentaje}%)`, 20, yTotal)
    doc.text(`-${formatCurrency(descuento)}`, 190, yTotal, { align: 'right' })
    yTotal += 5
  }

  // IVA (if any)
  if (ivaPorcentaje > 0) {
    doc.text(`IVA (${ivaPorcentaje}%)`, 20, yTotal)
    doc.text(formatCurrency(iva), 190, yTotal, { align: 'right' })
    yTotal += 5
  }

  // Separator
  doc.setDrawColor(255, 255, 255, 0.2)
  doc.setLineWidth(0.3)
  doc.line(20, yTotal, 190, yTotal)
  yTotal += 4

  // Total
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('TOTAL PROPUESTO', 20, yTotal)
  doc.setFontSize(16)
  doc.text(formatCurrency(totalFinal || 0), 190, yTotal, { align: 'right' })

  return doc
}
